import {Stack, Typography, useTheme} from '@mui/material';
import React, {useState} from 'react';
import {useNavigate} from 'react-router-dom';

import {Drawer} from 'presentation/components/Drawer';
import {HomeEnum} from 'presentation/constants';
import Icon from 'presentation/icons';
import {useMediaQueries} from 'presentation/pages/private/hooks';
import {
	CardInstructions,
	CardInstructionsContent,
} from 'presentation/pages/private/authorizations/components/CardInstructions';
import {CardInstructionsVariantEnum} from 'presentation/pages/private/authorizations/components/CardInstructions/types';
import {translations} from 'presentation/translations-new';

import {InstructionsSummary} from './styles';

const {keep, backToHome} = translations['pt-br'].commons;
const {instructionsTitle} =
	translations['pt-br'].privateFlow.requestAuthorizations.request.addAttachments;

type RequestInstructionsProps = {
	useDrawer: boolean;
	isOpen?: boolean;
	preventDismiss?: boolean;
	onContinue?: () => void;
	onBackToStart?: () => void;
	onClose?: () => void;
};

export function RequestInstructions({
	useDrawer,
	isOpen,
	preventDismiss = false,
	onContinue,
	onBackToStart,
	onClose,
}: RequestInstructionsProps) {
	const navigate = useNavigate();
	const {palette} = useTheme();
	const {isMobile} = useMediaQueries();
	const [internalOpenState, setInternalOpenState] = useState(false);
	const isDrawerOpen = isOpen ?? internalOpenState;

	const handleContinue = () => {
		if (onContinue) {
			onContinue();
			return;
		}

		setInternalOpenState(false);
	};

	const handleBackToStart = () => {
		if (onBackToStart) {
			onBackToStart();
			return;
		}

		navigate(HomeEnum.HOME_PAGE);
	};

	const handleRequestClose = () => {
		if (preventDismiss) return;

		if (onClose) {
			onClose();
			return;
		}

		setInternalOpenState(false);
	};

	const shouldRenderDrawer = useDrawer || isOpen;

	return (
		<>
			{useDrawer ? (
				<InstructionsSummary onClick={() => setInternalOpenState(true)}>
					<Typography variant="body1" fontWeight={600} color="text.secondary" pr={2}>
						{instructionsTitle}
					</Typography>
					<Icon name="expand-more-filled" size={20} color={palette.primary.main} />
				</InstructionsSummary>
			) : (
				<CardInstructions
					variant={CardInstructionsVariantEnum.SMALL}
					title={instructionsTitle}
					hideDescription
				/>
			)}

			{shouldRenderDrawer ? (
				<Drawer.Root
					open={isDrawerOpen}
					onOpen={() => setInternalOpenState(true)}
					onClose={handleRequestClose}
					width="600px"
					disableEscapeKeyDown={preventDismiss}
					disableSwipeToOpen
					customMaxHeight="calc(100% - 24px)">
					{isMobile ? null : (
						<Drawer.Header title={instructionsTitle} onClose={handleRequestClose} />
					)}
					<Drawer.Content gap={3}>
						{isMobile ? (
							<Typography variant="h6" fontWeight={600}>
								{instructionsTitle}
							</Typography>
						) : null}
						<Stack gap={3}>
							<CardInstructionsContent variant={CardInstructionsVariantEnum.SMALL} />
						</Stack>
					</Drawer.Content>
					<Drawer.Footer
						canStacks
						firstButtonTitle={backToHome}
						firstButtonOnClick={handleBackToStart}
						secondButtonTitle={keep}
						secondButtonOnClick={handleContinue}
					/>
				</Drawer.Root>
			) : null}
		</>
	);
}
